// Global, app-wide guard: blocks a space from ever being the first character
// typed into a text-like input/textarea, and strips any leading spaces that
// slip in via paste/autofill/programmatic value changes. Registered once
// (see src/app/app.ts) via document-level, capture-phase listeners rather
// than a per-component directive, so it automatically covers every input in
// the app — including ones added later — with zero per-form wiring.
//
// Deliberately scoped to plain textual fields: text/search/email/tel/url
// inputs (including inputs with no `type` attribute, which default to text)
// and <textarea>. Excluded on purpose:
// - type="password" — content is opaque to the user; silently rewriting it
//   could corrupt a password that legitimately starts with a space.
// - type="number"/"date"/etc., checkbox/radio/file/color/range — not
//   free-text fields, "leading space" has no meaning for them.
// - contenteditable rich-text editors (e.g. ngx-editor) — not <input>/
//   <textarea> elements, so they're never matched here; safely out of scope.
const TEXTUAL_INPUT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url', '']);

function isTextualField(target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement {
  if (target instanceof HTMLTextAreaElement) {
    return true;
  }
  return target instanceof HTMLInputElement && TEXTUAL_INPUT_TYPES.has(target.type);
}

/**
 * Attaches the guard to `doc`. Returns an unsubscribe function.
 */
export function installNoLeadingSpaceGuard(doc: Document): () => void {
  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key !== ' ') {
      return;
    }
    const target = event.target;
    if (!isTextualField(target) || target.selectionStart === null) {
      return;
    }
    // Everything currently before the caret — if it's empty or all
    // whitespace, this keystroke would insert (another) leading space.
    const before = target.value.slice(0, target.selectionStart);
    if (before.trim().length === 0) {
      event.preventDefault();
    }
  };

  // Fallback for paste / autofill / programmatic writes, which don't go
  // through `keydown`: strip any leading whitespace the value ends up with,
  // preserving the caret position relative to the (now-shifted) text.
  const onInput = (event: Event): void => {
    const target = event.target;
    if (!isTextualField(target)) {
      return;
    }
    const value = target.value;
    const trimmed = value.replace(/^\s+/, '');
    if (trimmed === value) {
      return;
    }
    const removed = value.length - trimmed.length;
    const caret = target.selectionStart ?? trimmed.length;
    target.value = trimmed;
    const newCaret = Math.max(0, caret - removed);
    try {
      target.setSelectionRange(newCaret, newCaret);
    } catch {
      // A handful of input types don't support setSelectionRange - ignore.
    }
  };

  // Capture phase so this runs *before* Angular's own native `input`
  // listener (attached directly on the element) reads `.value` — no need
  // to dispatch a synthetic follow-up event for forms/ngModel to pick up
  // the correction.
  doc.addEventListener('keydown', onKeydown, true);
  doc.addEventListener('input', onInput, true);

  return () => {
    doc.removeEventListener('keydown', onKeydown, true);
    doc.removeEventListener('input', onInput, true);
  };
}
